/**
 * Restful Client
 *
 * Ideally we want to refactor REST calls in this application to use
 * https://gitlab.com/aspendental/uiplatform/shared-ui/packages/-/blob/main/packages/shared-utils/src/utils/httpClient.ts?ref_type=heads
 * rather than this independent solution.  The blocker at the moment is that the latest @aspendental/shared-utils package is not compatible
 * with NextJS applications.  The theory we have for root cause is that shared-utils expects access to browser APIs that are not guaranteed
 * to exist within all areas of a NextJS application.
 *
 * Headers need to be disabled for requests that go to external sources like Google.
 *
 */
import { getSessionId } from '@aspendental/shared-utils/utils';
import { getCookie } from '@src/pages/utils/get-cookie';
import axios, { AxiosRequestConfig } from 'axios';

import { useAppStore } from '@src/store';

interface TAG_Headers {
	'X-Client-Application': string;
	'X-Session-Id': string;
	'X-Source': string;
}

type SupportedRequestTypes = 'get' | 'post';

interface RequestConfiguration {
	method: SupportedRequestTypes;
	url: string;
	headers?: TAG_Headers;
}

const createRequestConfiguration = (
	requestType: SupportedRequestTypes,
	url: string,
	disableTagHeaders: boolean,
	data: object | undefined = undefined
): RequestConfiguration => {
	const TagHeaders = {
		'X-Client-Application': process.env.APP_NAME,
		'X-Session-Id': getSessionId(),
		'X-Source': getCookie('isOneClickLogin') ? 'PortalCRM1Click' : 'Portal',
	};

	const config = {
		method: requestType,
		url,
		data,
	};

	return disableTagHeaders ? config : { headers: TagHeaders, ...config };
};

interface GetProps {
	domain?: string;
	route: string;
}

interface PostProps {
	domain?: string;
	route: string;
	data: object;
}

export default {
	get({ domain, route }: GetProps): Promise<any> {
		// eslint-disable-next-line
		return new Promise(async (resolve, reject) => {
			try {
				const uri = (domain ? domain : useAppStore.getState().restUrl) + route;

				const requestConfiguration = createRequestConfiguration('get', uri, !!domain) as AxiosRequestConfig;

				const result = await axios(requestConfiguration as AxiosRequestConfig);

				if (isNaN(result.status) || result.status < 200 || result.status > 299) {
					throw new Error(`GET: ${uri} returned invalid status: ${result.status}`);
				}

				resolve(result.data);
			} catch (e) {
				reject(e);
			}
		});
	},
	post({ domain, route, data }: PostProps): Promise<any> {
		// eslint-disable-next-line
		return new Promise(async (resolve, reject) => {
			try {
				const uri = (domain ? domain : useAppStore.getState().restUrl) + route;

				const requestConfiguration = createRequestConfiguration(
					'post',
					uri,
					!!domain,
					data
				) as AxiosRequestConfig;

				const result = await axios(requestConfiguration as AxiosRequestConfig);

				if (isNaN(result.status) || result.status < 200 || result.status > 299) {
					throw new Error(`POST: ${route} returned invalid status: ${result.status}`);
				}

				resolve(result.data);
			} catch (e) {
				reject(e);
			}
		});
	},
};
